// FloorSection — collapsible floor group inside the BuildingCapture
// rooms list (PRD §7.2.2, buildspec 20-ui §5.4b).
//
// Visual: redesigned per the design handoff. The header is a slim
// borderless button with a chevron + Fraunces floor name on the left
// and a `done/total` count pill on the right (no card chrome). The
// body is a vertical stack of `<RoomCard>` rows with no left rule —
// the cards already carry their own border, so an extra rule reads
// noisy. Collapsed state lives in component state; default-open
// matches the design.

import { ChevronRight } from 'lucide-react'
import { useMemo, useState, type ReactElement } from 'react'

import type { SpaceWithItems } from '@citycare/shared'

import { RoomCard } from './room-card'

export interface FloorSectionProps {
  floorName: string
  spaces: SpaceWithItems[]
  onRoomClick?: (space: SpaceWithItems) => void
  /**
   * When provided, each room renders a rename affordance that calls this
   * with the room being renamed (FFR2). Same owner + in-progress gating as
   * `onRoomDelete`; omitted otherwise so the control stays hidden.
   */
  onRoomEditAlias?: (space: SpaceWithItems) => void
  /**
   * When provided, each room renders a delete affordance that calls this
   * with the room being deleted (ADR-015). Passed through from the
   * building capture screen only for the session owner on an in-progress
   * building; omitted otherwise so the control stays hidden.
   */
  onRoomDelete?: (space: SpaceWithItems) => void
  defaultCollapsed?: boolean
}

export function FloorSection({
  floorName,
  spaces,
  onRoomClick,
  onRoomEditAlias,
  onRoomDelete,
  defaultCollapsed = false,
}: FloorSectionProps): ReactElement {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  // Serial first; same-serial rooms (two field-added rooms of different
  // types can share a serial) tie-break on room type then id, so the list
  // never reorders between renders or devices (audit-1-#15, sibling of the
  // #853 key fix). Mirrors the local-mirror ORDER BY in session-spaces-sql.ts
  // and the server's loadSpaces order.
  const sortedSpaces = useMemo(
    () =>
      [...spaces].sort(
        (a, b) =>
          a.serialNumber - b.serialNumber ||
          a.roomTypeId.localeCompare(b.roomTypeId) ||
          a.id.localeCompare(b.id),
      ),
    [spaces],
  )

  const { done, total } = useMemo(() => {
    let d = 0
    let t = 0
    for (const space of spaces) {
      for (const item of space.items) {
        t += 1
        if (parseFloat(item.quantity) > 0 || item.isZeroConfirmed) d += 1
      }
    }
    return { done: d, total: t }
  }, [spaces])

  const slug = floorName.toLowerCase().replace(/\s+/g, '-')
  const toggle = (): void => {
    setIsCollapsed((prev) => !prev)
  }

  return (
    <section
      className="space-y-2"
      data-testid={`floor-section-${slug}`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!isCollapsed}
        aria-controls={`floor-${slug}-rooms`}
        data-testid={`floor-section-header-${slug}`}
        className="flex min-h-11 w-full items-center justify-between gap-2 border-b border-[color:var(--color-cc-forest)]/10 px-1 pb-2.5 pt-1 text-left transition-colors hover:bg-[color:var(--color-cc-cream)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--color-cc-lime)]"
      >
        <span className="flex items-center gap-2">
          <ChevronRight
            aria-hidden="true"
            className={[
              'h-3.5 w-3.5 text-[color:var(--color-cc-charcoal)]/60 transition-transform',
              !isCollapsed ? 'rotate-90' : '',
            ].join(' ')}
          />
          <span className="font-display text-[15px] font-semibold text-[color:var(--color-cc-forest)]">
            {floorName}
          </span>
        </span>
        {/* Done/total count pill — skipped when the floor's rooms
             are all un-templated so the header doesn't show "0/0". */}
        {total > 0 ? (
          <span className="inline-flex items-center rounded-full bg-[color:var(--color-cc-cream-2)] px-2 py-0.5 font-mono text-[11px] font-bold tracking-wider text-[color:var(--color-cc-charcoal)]/70">
            {done}/{total}
          </span>
        ) : null}
      </button>

      {!isCollapsed ? (
        <div
          id={`floor-${slug}-rooms`}
          className="flex flex-col gap-2 pt-1"
          data-testid={`floor-${floorName.replace(/\s+/g, '-')}-rooms`}
        >
          {/* Key by space id, never by serial number: serials are per
               room type, so a floor with several room types has many
               spaces at serial 1. A duplicate key lets React drop or
               duplicate cards — a count-visibility risk. */}
          {sortedSpaces.map((space) => (
            <RoomCard
              key={space.id}
              space={space}
              onClick={() => onRoomClick?.(space)}
              onEditAlias={
                onRoomEditAlias !== undefined
                  ? () => onRoomEditAlias(space)
                  : undefined
              }
              onDelete={
                onRoomDelete !== undefined
                  ? () => onRoomDelete(space)
                  : undefined
              }
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
